import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Owner, OwnerInfo, Permissions} from 'auth/PermissionConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {EntitySummary} from 'constants/UserConstants';
import {Party} from 'parties/PartyConstants';

/**
 * @deprecated Use ReviewConstants.ReviewSummary where possible.
 */
export interface ReviewSummary {
  needsReview: number;
  sentForReview: number;
  inProgress: number;
  rejected: number;
  retracted: number;
  accepted: number;
  ids: {
    needsReview: Array<string>;
    sentForReview: Array<string>;
    inProgress: Array<string>;
    rejected: Array<string>;
    retracted: Array<string>;
    accepted: Array<string>;
  };
}

const PROJECT_TYPE = 'project' as const;
const REGISTRY_TYPE = 'registry' as const;
export type SourceType = typeof PROJECT_TYPE | typeof REGISTRY_TYPE;

export interface SourceSummaryWithoutRootFolder {
  id: string;
  archived: boolean;
  archiveRecord: ArchiveRecord | null;
  createdAt: string;
  creator: EntitySummary | null;
  hidden: boolean;
  starred: boolean;
  locked: boolean;
  modifiedAt: string;
  name: string;
  ownerInfo: OwnerInfo;
  public: boolean;
  alwaysShowAncestorFolderNames?: boolean;
  // This is always a NumberIDFolder when returned by the server, but the client sometimes constructs it from
  // a StringIDFolder.
  rootFolder: NumberIDFolder | StringIDFolder;
  s_owner: Owner;
  sourceType: SourceType;
  url: string;
  reviewLifecyclesIds?: Array<string>;
}

export interface SourceSummary extends SourceSummaryWithoutRootFolder {
  // This is always a NumberIDFolder when returned by the server, but the client sometimes constructs it from
  // a StringIDFolder.
  rootFolder: NumberIDFolder | StringIDFolder;
}

export interface Source extends SourceSummary {
  description: string | null;
}

// This is the superset of what an actual Source might contain. This simplifies type definitions since users
// can pluck the known fields off of it. (Source.to_dict() has a few flags, and is also often called with
// permissions attached, e.g. nav.api.get_sources() calls Source.batch_to_dict_with_perms())
export interface FullSource extends Source {
  collaborators: Array<Party>;
  permissions: Permissions;
  reviewSummary: ReviewSummary;
}

// Returned by FolderDAO.fetchAll as part of BaseMinimalFolder, a model with a small subset of attributes
// optimized for bulk performance.
export interface MinimalSourceSummary {
  id: string;
  archived: boolean;
  archiveRecord: ArchiveRecord | null;
  hidden: boolean;
  starred: boolean;
  sourceType: SourceType;
}

// Returned by fetchSources(), and what lives in NavStore.
export type NavStoreSource = Omit<FullSource, 'reviewSummary'>;

export type SidepanelItemSource = Omit<NavStoreSource, 'description' | keyof Omit<Permissions, 'owner'>> & {
  creator_handle?: string;
  description?: string | null;
};

export interface ProjectSummary extends SourceSummary {
  sourceType: typeof PROJECT_TYPE;
  /** @deprecated */
  reviewLifecycleId?: string;
  reviewLifecyclesIds: Array<string>;
}

export type Project = ProjectSummary & Source;

export type NavStoreProject = Project & NavStoreSource;

export const STARRED = 'starred';
export const PERSONAL = 'personal';
export const TEAM = 'team';
export const ALL = 'all';
export const ORGANIZATION = 'organization';
export const HIDDEN = 'hidden';
export const ARCHIVED = 'archived';
export {PROJECT_TYPE, REGISTRY_TYPE};
